import { EVENT_SCHEMAS, type EventName, type EventProps, isEventName } from "@charter/shared";
import { IS_DEV } from "@/config/env";

/**
 * Telemetry event bus. Events fan out to sinks; the built-in sinks are a ring buffer (debug menu)
 * and the console in dev. Other sinks (e.g. Sentry breadcrumbs in platform/crash/sentry.ts) register
 * with `addTelemetrySink`. Never put PII in props.
 *
 * Event names and props come from the taxonomy in `@charter/shared` (`events.ts`): a known name gets
 * its props type-checked at the call site, and `telemetry.test.ts` fails on any literal that is
 * not in the taxonomy. Unknown names still compile so a sink can be prototyped, but the test is
 * the gate: add the event to the taxonomy before it ships.
 */
/** @public */
export interface TelemetryEvent {
  name: string;
  props: Record<string, string | number | boolean | null>;
  at: number;
}

/** @public */
export type TelemetrySink = (event: TelemetryEvent) => void;

type Props = TelemetryEvent["props"];

/** Known events take their declared props (optional when none are required); others are loose. */
type TrackArgs<N extends string> = N extends EventName
  ? Record<never, never> extends EventProps<N>
    ? [props?: EventProps<N>]
    : [props: EventProps<N>]
  : [props?: Props];

const RING_SIZE = 200;
const ring: TelemetryEvent[] = [];
const sinks = new Set<TelemetrySink>();
const listeners = new Set<() => void>();

export function track<N extends string>(name: N, ...args: TrackArgs<N>): void {
  const props: Props = (args as [Props?])[0] ?? {};
  if (IS_DEV && isEventName(name)) {
    const parsed = EVENT_SCHEMAS[name].safeParse(props);
    if (!parsed.success) console.warn(`[telemetry] ${name} props do not match the taxonomy`, props);
  }
  const event: TelemetryEvent = { name, props, at: Date.now() };
  ring.push(event);
  if (ring.length > RING_SIZE) ring.shift();
  for (const sink of sinks) {
    try {
      sink(event);
    } catch (err) {
      console.warn("[telemetry] sink failed", err);
    }
  }
  for (const l of listeners) l();
}

/** @public Extension point: register analytics/crash sinks at boot. */
export function addTelemetrySink(sink: TelemetrySink): () => void {
  sinks.add(sink);
  return () => sinks.delete(sink);
}

/** @public Debug-menu access to recent events. Returns a snapshot copy, newest first. */
export function recentTelemetry(): readonly TelemetryEvent[] {
  return [...ring].reverse();
}

if (IS_DEV) {
  addTelemetrySink((e) => console.info(`[telemetry] ${e.name}`, e.props));
}
